import { CurrencyInput, toCents } from '@fox/atoms/src/inputs/currency-input';
import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { Skeleton } from '@fox/atoms/src/skeleton/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import { IBlockDefinition, IBlockProps } from '@fox/blocks/contracts/block';
import { FormFinancialLineItem } from '@fox/finance/src/contracts';
import { formatCurrency } from '@fox/finance/src/helpers';
import { FlexColumn, FlexColumnSpaced, FlexRowSmallSpaced } from '@fox/layout/src/flex';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { PAYMENT_BLOCK_ANSWER_TYPE, TRANSACTION_BLOCK_TYPE } from '../../../form-block-types';
import { findRenderDependencies, getAttributeValues, transformLegacyProps } from '../../../utils';
import { useFormFinancialContext } from '../../form-financial-context/form-financial-context';
import { IField, IQuestionAnswer } from '../../form-root/contracts';
import { FormRenderMode } from '../../form-runtime-context/contracts';
import { useFormRuntimeContext } from '../../form-runtime-context/form-runtime-context';
import { useFormBlockReRenderer } from '../../use-form-block-re-renderer';
import { useConditionEvaluator } from '../form-block/conditionals/helpers';
import { checkSuccessfulPayment } from '../payment-block/helpers';
import { TransactionPropsType } from './contracts';
import { alignDictionary, justifyDictionary, SpecialButton } from './styled-components';

export function prefillTransactionAnswer(query: URLSearchParams, question: any): IQuestionAnswer | undefined {
	const answer = query.get(question.name);
	if (!answer) return undefined;

	const props = tryParseJSONObject<TransactionPropsType>(question.metadataJson);
	if (!props) {
		console.warn('Transaction block metadata is not valid JSON:', question.metadataJson);
		return undefined;
	}

	return buildAnswer(answer, question.name, props);
}

function buildAnswer(amount: string, questionId: any, props: TransactionPropsType) {
	return {
		answer: {
			answerString: `${amount}`
		},
		questionName: questionId,
		data: {
			lineItems: [
				{
					// We need to have the default currency, but we don't have this at this time - given the urgency and our current needs this will do
					amount: toCents(amount),
					comment: props.comment,
					lineTypeId: props.lineTypeId,
					immediatePayment: true,
					invoice: props.invoice
				}
			]
		}
	};
}

export function TransactionBlock({ props, type }: IBlockProps<TransactionPropsType>) {
	const { fetchCurrencies } = useFormFinancialContext();
	const { formRenderMode, getAnswer, getAnswers, answerQuestion, sections } = useFormRuntimeContext();
	const dependencies = findRenderDependencies(sections, [PAYMENT_BLOCK_ANSWER_TYPE]);
	useFormBlockReRenderer([props.name, ...dependencies], props as unknown as IField); //sigh
	const theme = useTheme();
	const {
		attributes: { padding, margin }
	} = transformLegacyProps(type, props);
	const { amounts = [], hideOther = false, otherAmoutLabel } = props;
	const { data: currencies, isLoading: currenciesLoading } = useQuery({
		queryKey: ['fetchCurrencies'],
		queryFn: async () => {
			const result = await fetchCurrencies();
			if (!result?.default) throw Error('Unable to fetch supported currencies');
			return result;
		},
		staleTime: Infinity
	});

	const { top: paddingTop, bottom: paddingBottom, right: paddingRight, left: paddingLeft } = getAttributeValues(padding);
	const { top: marginTop, bottom: marginBottom, right: marginRight, left: marginLeft } = getAttributeValues(margin);

	// If there is a condition check it matches the required value before rendering the question
	const conditionValid = useConditionEvaluator(props as unknown as IField); //there's gotta be a better way

	const styleObj = {
		paddingTop,
		paddingLeft,
		paddingBottom,
		paddingRight,
		marginLeft,
		marginTop,
		marginRight,
		marginBottom
	};

	const data =
		props ??
		({
			padding: 5,
			borderRadius: 5
		} as TransactionPropsType);
	const style = data?.style;
	// todo get primary out of the form style config
	const background = style?.backgroundColor ?? theme?.colors.secondary;

	const hoverBackgroundColour = style?.hoverBackgroundColour;
	const selectedBackgroundColour = style?.selectedColor;
	const color = style?.color ?? theme.colors.white;
	const buttonsAlignment = data.attributes?.horizontalAlign;

	const currentAnswer: IQuestionAnswer<{ lineItems?: FormFinancialLineItem }> | undefined = getAnswer(props.name);
	const [amount, setAmountState] = useState<string | undefined>(currentAnswer?.answer?.answerString);
	const [buttonAmount, setButtonAmountState] = useState<string | undefined>(amount);

	const [otherSelected, setOtherSelected] = useState<boolean>(false);

	const answers = getAnswers();
	const hasSuccessfulPayment = checkSuccessfulPayment(answers);

	// This stupid check is needed bcs apparently we are delaling with two different checks depending on the view mode
	// In Preview - Our local state comes to play
	// in Design - we are actually updating props
	// both check separately would work only for one of the views, and || or && would not work either

	const amountMatchesOption = amounts.some(a => `${a}` === amount);

	function setAmount(amount: string, button?: boolean) {
		// force the key to change when clicking this so the CurrencyInput re-renders with a new defaultValue
		if (button) {
			setButtonAmountState(prev => (amount === prev ? `${amount}${Math.random()}` : amount));
		}
		setAmountState(amount);

		answerQuestion(buildAnswer(amount, props.name, props));
	}

	if (!conditionValid) {
		return null;
	}

	if (hasSuccessfulPayment) {
		return (
			<MuiAlert severity="success">
				<Typography>Payment has been processed, amount can no longer be changed.</Typography>
			</MuiAlert>
		);
	}
	if (currenciesLoading) return <Skeleton height="3rem" />;
	return (
		<FlexColumn
			style={{
				...styleObj,
				alignItems: data?.containerHorizontalAlign ? alignDictionary[data?.containerHorizontalAlign] : 'center'
			}}
		>
			<FlexColumnSpaced
				style={{
					...styleObj,
					pointerEvents: formRenderMode !== FormRenderMode.design ? 'all' : 'none',
					maxWidth: `${data?.containerMaxWidth}%`
				}}
			>
				<FlexRowSmallSpaced
					style={{
						justifyContent: buttonsAlignment ? justifyDictionary[buttonsAlignment] : 'flex-start',
						flexWrap: 'wrap'
					}}
				>
					{amounts.map((a, index) => (
						<SpecialButton
							key={`amount_button_${index}_${a}`}
							type="button"
							$borderRadius={data.borderRadius}
							$fontFamily={style?.fontFamily}
							$fontSize={style?.fontSize}
							$borderSize={style?.borderSize}
							$borderColor={style?.borderColor}
							$fontWeight={style?.fontWeight}
							$fontStyle={style?.fontStyle}
							$textDecoration={style?.textDecoration}
							$background={background}
							$color={color}
							$hoverBackgroundColour={hoverBackgroundColour}
							$selected={`${a}` === amount}
							$selectedBackgroundColour={selectedBackgroundColour}
							$minWidth={data.buttonMinWidth}
							onClick={() => {
								setAmount(`${a}`, true);

								setOtherSelected(false);
							}}
						>
							{formatCurrency(toCents(a /* we know this is in dollars */ as string), currencies?.default)}
						</SpecialButton>
					))}
					{!hideOther || !amountMatchesOption ? (
						<SpecialButton
							type="button"
							$borderRadius={data.borderRadius}
							$fontFamily={style?.fontFamily}
							$selected={!amountMatchesOption || otherSelected}
							$fontSize={style?.fontSize}
							$borderSize={style?.borderSize}
							$borderColor={style?.borderColor}
							$fontWeight={style?.fontWeight}
							$fontStyle={style?.fontStyle}
							$textDecoration={style?.textDecoration}
							$background={background}
							$color={color}
							$hoverBackgroundColour={hoverBackgroundColour}
							$selectedBackgroundColour={selectedBackgroundColour}
							$minWidth={data.buttonMinWidth}
							onClick={() => {
								setAmount(``, true);
								setOtherSelected(true);
							}}
						>
							{otherAmoutLabel ?? 'Other amount'}
						</SpecialButton>
					) : null}
				</FlexRowSmallSpaced>
				{hideOther && !amountMatchesOption ? null : (
					<FlexRowSmallSpaced
						style={{
							justifyContent: buttonsAlignment ? justifyDictionary[buttonsAlignment] : 'flex-start',
							flexWrap: 'wrap'
						}}
						key={`show_other_input_${hideOther}`}
					>
						<CurrencyInput
							label={otherAmoutLabel || 'Other amount'}
							size="small"
							sx={{ minWidth: '150px', ...(data.buttonMinWidth ? { width: data.buttonMinWidth } : {}) }}
							positiveOnly
							defaultValue={amount}
							key={`amount_${buttonAmount}`}
							setAmount={setAmount}
						/>
					</FlexRowSmallSpaced>
				)}
			</FlexColumnSpaced>
		</FlexColumn>
	);
}

export const TransactionBlockRuntime: IBlockDefinition = {
	type: TRANSACTION_BLOCK_TYPE,
	Component: TransactionBlock
};
